/**
 * Browser-side client for the translation-review API. Used by both the
 * backoffice catalogue (P1) and the in-place overlay (P3) so there is exactly
 * one write path, one status contract, and one place ICU errors surface.
 *
 * Type-only imports from core/types keep this bundle free of server code.
 */
import type {
  CatalogueEntry,
  OverrideRecord,
  ReviewLocale,
  ReviewStatus,
} from '../core/types';

export interface CatalogueResponse {
  rows: CatalogueEntry[];
  total: number;
  totalCatalogue: number;
  locales: ReviewLocale[];
}

export interface CatalogueQuery {
  locale?: ReviewLocale;
  status?: ReviewStatus;
  search?: string;
  modifiedSince?: string;
}

export class SaveOverrideError extends Error {
  /** ICU parity messages, present when the save was rejected for parity. */
  icuErrors: string[];
  status: number;
  constructor(message: string, opts: { icuErrors?: string[]; status: number }) {
    super(message);
    this.name = 'SaveOverrideError';
    this.icuErrors = opts.icuErrors ?? [];
    this.status = opts.status;
  }
}

const BASE = '/api/i18n-review';

export async function fetchCatalogue(query: CatalogueQuery = {}): Promise<CatalogueResponse> {
  const params = new URLSearchParams();
  if (query.locale) params.set('locale', query.locale);
  if (query.status) params.set('status', query.status);
  if (query.search) params.set('search', query.search);
  if (query.modifiedSince) params.set('modifiedSince', query.modifiedSince);
  const res = await fetch(`${BASE}/catalogue?${params.toString()}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`Failed to load catalogue (${res.status})`);
  }
  return res.json();
}

export async function fetchEntry(id: string): Promise<CatalogueEntry> {
  const res = await fetch(`${BASE}/entry?id=${encodeURIComponent(id)}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) {
    throw new Error(`Failed to load message "${id}" (${res.status})`);
  }
  const data = await res.json();
  return data.row as CatalogueEntry;
}

export async function saveOverride(input: {
  id: string;
  locale: ReviewLocale;
  value: string;
}): Promise<OverrideRecord> {
  const res = await fetch(`${BASE}/override`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new SaveOverrideError(data?.error ?? `Save failed (${res.status})`, {
      icuErrors: data?.icuErrors,
      status: res.status,
    });
  }
  return data.record as OverrideRecord;
}
